var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { Drawer, Typography, Skeleton, Flex, MarkdownRenderer, theme, } from '@robinpowered/ui-kit';
import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@awesome.me/kit-cf858715fd/icons/classic/regular';
import { SpaceCard } from './components/Space/SpaceCard';
import { EventIdRow } from './components/EventIdRow';
import { AttendeesCard } from './components/AttendeesCard';
import { EventRsvpActions } from './components/EventRsvpActions/EventRsvpActions';
import { EventDetailContextProvider, useEventDetailContext, } from './context/EventDetailContextProvider';
import { RecurrenceText } from './components/RecurrenceText';
import { FooterButtons } from './components/FooterButtons';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from '../context/ConfigContext';
import { useRequiredContexts } from '../hooks/useRequiredContexts';
import { LocalizationContext } from '../context/LocalizationContext';
import { DeleteModal } from './components/DeleteModal';
import moment from 'moment';
import { splitEventDescription } from '../utils/parsers';
import { VideoConferenceCard } from './components/VideoConferenceCard';
var DATE_FORMAT = 'ddd, MMM D';
/**
 * Inner content of EventDetails without any container chrome (no Drawer, no
 * Sider — just the body + footer + DeleteModal portal). Use this when you
 * want to render the details inside your own layout primitive (e.g. the
 * shell's right-hand `Sider`).
 *
 * Must be rendered inside `EventDetailContextProvider`, `ConfigProvider`,
 * and `LocalizationContext` — `EventDetailsContent` (the top-level wrapper)
 * sets those up for you.
 */
var EventDetailsContentBody = function (_a) {
    var _b, _c, _d, _e;
    var onClose = _a.onClose, onClickEdit = _a.onClickEdit, onDelete = _a.onDelete, timeFormat = _a.timeFormat;
    var t = useTranslation('meetingRooms').t;
    var _f = useEventDetailContext(), event = _f.event, loadingEvent = _f.loadingEvent, showDeleteModal = _f.showDeleteModal, setShowDeleteModal = _f.setShowDeleteModal, parsedDateTime = _f.parsedDateTime;
    var token = theme.useToken().token;
    var deletingAsSpace = !!((_b = event === null || event === void 0 ? void 0 : event.event_permissions) === null || _b === void 0 ? void 0 : _b.remove_space) &&
        !(event === null || event === void 0 ? void 0 : event.event_permissions.cancel);
    var formattedDateTime = useMemo(function () {
        return {
            date: parsedDateTime === null || parsedDateTime === void 0 ? void 0 : parsedDateTime.date.format(DATE_FORMAT),
            time: (parsedDateTime === null || parsedDateTime === void 0 ? void 0 : parsedDateTime.startTime.format(timeFormat)) +
                ' - ' +
                (parsedDateTime === null || parsedDateTime === void 0 ? void 0 : parsedDateTime.endTime.format(timeFormat)),
        };
    }, [parsedDateTime, timeFormat]);
    /* microsoft_365 or google or none */
    var syncedText = useMemo(function () {
        /* synced vs manual */
        if ((event === null || event === void 0 ? void 0 : event.creation_type) !== 'synced')
            return null;
        var remoteType = (event === null || event === void 0 ? void 0 : event.remote_type.includes('microsoft'))
            ? 'Microsoft 365'
            : (event === null || event === void 0 ? void 0 : event.remote_type.includes('google'))
                ? 'Google'
                : null;
        return (event === null || event === void 0 ? void 0 : event.remote_type) && remoteType
            ? t('eventDetails.synced_text', { remoteType: remoteType })
            : t('eventDetails.synced_text_no_remote_type');
    }, [event === null || event === void 0 ? void 0 : event.remote_type, event === null || event === void 0 ? void 0 : event.creation_type, t]);
    // Strip the provider-injected integration block (rendered by the video
    // conference card) so the description shows only the user's own text.
    var userDescription = useMemo(function () { return splitEventDescription(event === null || event === void 0 ? void 0 : event.description).userDescription; }, [event === null || event === void 0 ? void 0 : event.description]);
    var teamsIntegration = useMemo(function () {
        var _a, _b;
        return (_b = (_a = event === null || event === void 0 ? void 0 : event.integrations) === null || _a === void 0 ? void 0 : _a.find(function (integration) { var _a; return ((_a = integration.integration) === null || _a === void 0 ? void 0 : _a.type) === 'teams'; })) !== null && _b !== void 0 ? _b : null;
    }, [event === null || event === void 0 ? void 0 : event.integrations]);
    if (loadingEvent || !event) {
        return (_jsx(BodyContainer, { children: _jsx(Skeleton, { active: true }) }));
    }
    return (_jsxs(_Fragment, { children: [showDeleteModal && (_jsx(DeleteModal, { eventId: event.id, eventStartTime: moment(event.start.date_time), seriesId: event.series_id, onDelete: function () {
                    onDelete();
                    onClose();
                }, onClose: function () {
                    setShowDeleteModal(false);
                }, removeSpace: deletingAsSpace, 
                /* Proxied event ids have no top-level space_id; fall back to the
                   first space invitee's space */
                spaceId: (_c = event.space_id) !== null && _c !== void 0 ? _c : (_e = (_d = event.space_invitees) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.space_id, remoteType: event.remote_type })), _jsxs(BodyContainer, { children: [_jsxs(EventHeader, { children: [_jsx(Typography.Title, { level: 3, children: (event === null || event === void 0 ? void 0 : event.title) || t('eventDetails.untitled_event') }), _jsxs(DateTimeSection, { children: [_jsxs(Flex, { align: "center", gap: "8px", children: [_jsx(FontAwesomeIcon, { icon: faCalendar, size: "sm", color: token.colorText }), _jsx(Typography.Text, { children: t('eventDetails.formattedDateAndTime.date', {
                                                    date: formattedDateTime.date,
                                                    time: formattedDateTime.time,
                                                }) })] }), _jsx(RecurrenceText, {})] })] }), _jsx(EventRsvpActions, {}), _jsx(SpaceCard, { timeFormat: timeFormat }), _jsx(AttendeesCard, {}), _jsx(VideoConferenceCard, { teamsIntegration: teamsIntegration }), (event === null || event === void 0 ? void 0 : event.description) && (_jsx(EventDescription, { markdownString: teamsIntegration ? userDescription : event === null || event === void 0 ? void 0 : event.description })), syncedText && (_jsx(Typography.Text, { size: "small", type: "secondary", children: syncedText })), _jsx(EventIdRow, { eventId: event.id })] }), _jsx(FooterButtons, { onClickEdit: onClickEdit })] }));
};
/**
 * `EventDetailsContent`: the chrome-free variant of `EventDetails`. Renders
 * the body, footer buttons, and (when active) the delete modal — but no
 * Drawer or Sider wrapper. Use this when you want to host event details
 * inside your own panel (e.g. the shell's right-hand `Sider`).
 *
 * Behavior is otherwise identical to `EventDetails`: it sets up the same
 * `ConfigProvider`, `EventDetailContextProvider`, `LocalizationContext`, and
 * required-contexts validation.
 */
export var EventDetailsContent = function (props) {
    var _a;
    useRequiredContexts();
    return (_jsx(LocalizationContext, { children: _jsx(ConfigProvider, { config: props.config, children: _jsx(EventDetailContextProvider, { eventId: props.eventId, timezone: props.timezone, onClose: props.onClose, onError: props.onError, onDelete: props.onDelete, onSuccess: props.onSuccess, onTrackComposerEvent: props.onTrackComposerEvent, children: _jsx(EventDetailsContentBody, { onClose: props.onClose, onClickEdit: props.onClickEdit, onDelete: props.onDelete, timeFormat: (_a = props.timeFormat) !== null && _a !== void 0 ? _a : '' }) }) }) }));
};
var EventDetailsWrapper = function (_a) {
    var onClose = _a.onClose, onClickEdit = _a.onClickEdit, onDelete = _a.onDelete, timeFormat = _a.timeFormat;
    var t = useTranslation('meetingRooms').t;
    return (_jsx(StyledDrawer, { title: t('eventDetails.title'), onClose: onClose, open: true, mask: false, children: _jsx(EventDetailsContentBody, { onClose: onClose, onClickEdit: onClickEdit, onDelete: onDelete, timeFormat: timeFormat }) }));
};
/**
 * Internal implementation of EventDetails.
 * Assumes all required contexts (ApolloProvider, etc.) are already present.
 */
var EventDetailsInternal = function (_a) {
    var timeFormat = _a.timeFormat, timezone = _a.timezone, eventId = _a.eventId, onClose = _a.onClose, onClickEdit = _a.onClickEdit, onDelete = _a.onDelete, onError = _a.onError, config = _a.config, onSuccess = _a.onSuccess, onTrackComposerEvent = _a.onTrackComposerEvent;
    return (_jsx(ConfigProvider, { config: config, children: _jsx(EventDetailContextProvider, { eventId: eventId, timezone: timezone, onClose: onClose, onError: onError, onDelete: onDelete, onSuccess: onSuccess, onTrackComposerEvent: onTrackComposerEvent, children: _jsx(EventDetailsWrapper, { onClose: onClose, onClickEdit: onClickEdit, onDelete: onDelete, timeFormat: timeFormat !== null && timeFormat !== void 0 ? timeFormat : '' }) }) }));
};
/**
 * EventDetails component with context validation.
 *
 * This component validates that all required React contexts (e.g., ApolloProvider)
 * are present before rendering. If contexts are missing, it throws a helpful error
 * message with setup instructions.
 *
 * @example
 * ```tsx
 * import { ApolloProvider } from '@apollo/client';
 * import { EventDetails } from '@robinpowered/dashboard-apps-patterns';
 *
 * <ApolloProvider client={apolloClient}>
 *   <EventDetails
 *     config={config}
 *     eventId={eventId}
 *     timezone="America/New_York"
 *     timeFormat="h:mm a"
 *     onClose={handleClose}
 *     onDelete={handleDelete}
 *     onClickEdit={handleEdit}
 *     onError={handleError}
 *   />
 * </ApolloProvider>
 * ```
 */
export var EventDetails = function (props) {
    // Validate required contexts before rendering
    useRequiredContexts();
    // If validation passes, render the internal component
    return (_jsx(LocalizationContext, { children: _jsx(EventDetailsInternal, __assign({}, props)) }));
};
var StyledDrawer = styled(Drawer)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  .ant-drawer-header-title {\n    align-items: center;\n    display: flex;\n    flex-direction: row-reverse;\n  }\n\n  .ant-drawer-body {\n    padding: 0;\n    display: flex;\n    flex-direction: column;\n  }\n"], ["\n  .ant-drawer-header-title {\n    align-items: center;\n    display: flex;\n    flex-direction: row-reverse;\n  }\n\n  .ant-drawer-body {\n    padding: 0;\n    display: flex;\n    flex-direction: column;\n  }\n"])));
var BodyContainer = styled(Flex)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  flex: 1;\n  padding: 16px;\n  flex-direction: column;\n  gap: 16px;\n  overflow-y: auto;\n"], ["\n  flex: 1;\n  padding: 16px;\n  flex-direction: column;\n  gap: 16px;\n  overflow-y: auto;\n"])));
var EventHeader = styled(Flex)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  flex-direction: column;\n  gap: 4px;\n"], ["\n  flex-direction: column;\n  gap: 4px;\n"])));
var DateTimeSection = styled(Flex)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  flex-direction: column;\n  gap: 4px;\n"], ["\n  flex-direction: column;\n  gap: 4px;\n"])));
var StyledMarkdownRenderer = styled(MarkdownRenderer)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ul {\n    padding: 0 16px;\n    list-style: disc !important;\n  }\n  ol {\n    padding: 0 16px;\n    list-style: decimal !important;\n  }\n\n  strong {\n    font-weight: bold;\n  }\n\n  em,\n  i {\n    font-style: italic;\n  }\n"], ["\n  ul {\n    padding: 0 16px;\n    list-style: disc !important;\n  }\n  ol {\n    padding: 0 16px;\n    list-style: decimal !important;\n  }\n\n  strong {\n    font-weight: bold;\n  }\n\n  em,\n  i {\n    font-style: italic;\n  }\n"])));
// Providers send "empty" descriptions as blank wrapper markup; skip those.
var EventDescription = function (_a) {
    var markdownString = _a.markdownString;
    if (!markdownString) {
        return null;
    }
    var text = markdownString
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;/gi, '')
        .replace(/[\s\u200b]/g, '');
    if (!text) {
        return null;
    }
    return _jsx(StyledMarkdownRenderer, { markdownString: markdownString });
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
